import React, { useEffect, useRef, useReducer, useCallback, Ref } from 'react'
import { Menu, MenuItem, Position, Checkbox } from '@blueprintjs/core'
import { Popover2Props } from '@blueprintjs/popover2'
import { Select2 } from '@blueprintjs/select'
import { Button, Flex, IconButton, Toaster } from '..'
import ClearableSelectWrapper from './ClearableSelectWrapper'
import { get, debounce, orderBy } from 'lodash'
import { IUIButtonProps } from '@ui/buttons/Button'
import { IPageInfo, IEdge, IRecord } from '@api/types'
import { useIntersect } from '@hooks'

export type ItemProps<T> = {
  type?: 'skeleton' | 'value'
  data?: T
  query?: string
  refItem?: Ref<HTMLDivElement>
}

export type ListFuc<T> = ({ query, after }: { query: string; after?: string }) => Promise<{
  pageInfo: IPageInfo
  edges: IEdge<T>[]
}>

export interface SelectProps<T> {
  value: T | null
  renderer?: React.ReactNode
  multi?: boolean
  placeholder?: string
  clearable?: boolean
  filterable?: boolean
  popoverProps?: Partial<Popover2Props>
  onCreate?: (item: string) => void
  canCreate?: boolean
  buttonProps?: IUIButtonProps
  onChange: (item: T | null) => void
  itemDisabled?: (item: T) => boolean
  itemListPredicate?: (query: string, items: T[]) => T[]
  minPopoverWidth?: number
  minItemWidth?: number
  popoverBottomContent?: React.ReactNode
  onClose?: (hook: Hook<IRecord>) => void
}

export interface GenericSelectWithInfiniteScrollProps<T> extends SelectProps<T> {
  Item: React.FC<React.PropsWithChildren<ItemProps<T>>>
  list: ListFuc<T>
  onClose?: (hook: Hook<IRecord>) => void
}
const GenericSelect = Select2

export default function GenericSelectWithInfiniteScroll<T extends IRecord>({
  value,
  multi,
  clearable,
  buttonProps,
  placeholder,
  renderer,
  canCreate,
  onCreate,
  onChange,
  itemDisabled,
  list,
  Item,
  popoverBottomContent,
  popoverProps,
  minPopoverWidth = 313,
  minItemWidth = 316,
  filterable = true,
  itemListPredicate,
  onClose,
}: GenericSelectWithInfiniteScrollProps<T>) {
  const hook = useSelectWithInfiniteScroll<T>(list)
  const { refItem, refRoot } = useIntersect({
    threshold: [0.75],
    callback: hook.next,
  })

  const NoResults =
    hook.query && canCreate ? (
      <MenuItem
        style={{ minWidth: 316 }}
        onClick={() => {
          onCreate?.(hook.query || '')
          hook.run('')
        }}
        text={`Create option ${hook.query || ''}`}
      />
    ) : (
      <MenuItem disabled style={{ minWidth: minItemWidth }} text="No results" />
    )

  return (
    <ClearableSelectWrapper>
      <GenericSelect<T>
        resetOnSelect
        filterable={filterable}
        inputProps={{ placeholder: 'Search...' }}
        popoverProps={{
          position: Position.BOTTOM,
          minimal: true,
          ...popoverProps,
          onClose: () => {
            onClose && onClose(hook as unknown as Hook<IRecord>)
          },
        }}
        query={hook.query}
        onQueryChange={hook.run}
        activeItem={hook.activeItem}
        onActiveItemChange={hook.setActiveItem}
        noResults={NoResults}
        items={hook.results}
        itemListPredicate={itemListPredicate}
        itemListRenderer={({ filteredItems, renderItem }) => {
          return (
            <div ref={refRoot} style={{ paddingTop: 5, minWidth: minPopoverWidth }}>
              <Menu>
                {filteredItems.map((item, i) => renderItem(item, i))}
                {(hook.pageInfo.hasNextPage ||
                  (hook.status === 'loading' && !hook.results.length)) &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <MenuItem
                      key={i}
                      text={
                        <Flex align="center">
                          {multi && <Checkbox />}
                          <Item type="skeleton" />
                        </Flex>
                      }
                    />
                  ))}
                {!filteredItems.length && hook.status === 'idle' && NoResults}
              </Menu>
              {popoverBottomContent}
            </div>
          )
        }}
        itemRenderer={(item, { handleClick, modifiers, query, index }) => (
          <MenuItem
            shouldDismissPopover={multi ? false : true}
            active={modifiers.active}
            disabled={modifiers.disabled}
            key={item.id}
            onClick={(e: React.MouseEvent<any>) => {
              if (multi) {
                e.preventDefault()
                onChange(item)
                return
              }
              handleClick(e)
            }}
            text={
              <div className="flex flex-grow items-center">
                {multi && <Checkbox checked={itemDisabled?.(item)} />}
                <Item
                  data={item}
                  query={query}
                  refItem={
                    (index === hook.results.length - 1
                      ? refItem
                      : undefined) as React.Ref<HTMLDivElement>
                  }
                />
              </div>
            }
          />
        )}
        onItemSelect={onChange}>
        {renderer ? (
          renderer
        ) : (
          <Button
            {...buttonProps}
            rightIconName={!get(buttonProps, 'iconName') && !value ? 'chevron-down' : undefined}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              ...buttonProps?.style,
            }}
            text={value ? <Item type="value" data={value} /> : placeholder || 'Select'}
          />
        )}
      </GenericSelect>
      {value && clearable && (
        <IconButton
          minimal
          data-button="clear-button"
          iconName="times"
          tooltip="Clear"
          onClick={() => onChange(null)}
        />
      )}
    </ClearableSelectWrapper>
  )
}

interface SearchAction {
  type: 'search'
  query: string
}

interface ResetAction<T> {
  type: 'reset'
  pageInfo: IPageInfo
  results: T[]
  query: string
  after: string
  activeItem?: any
}

interface LoadAction<T> {
  type: 'load'
  pageInfo: IPageInfo
  results: T[]
}

interface ActiveItemUpdateAction<T> {
  type: 'activeItemUpdate'
  activeItem: T | null
}

interface PageAction {
  type: 'page'
}

type Action<T> =
  | SearchAction
  | LoadAction<T>
  | PageAction
  | ActiveItemUpdateAction<T>
  | ResetAction<T>

interface State<T> {
  status: string
  query: string
  after: string
  pageInfo: IPageInfo
  results: T[]
  activeItem: T | null
}

interface Hook<T extends IRecord> extends State<T> {
  setActiveItem: (acitveItem: T | null) => void
  run: (query: string) => void
  next: () => void
  reset: () => void
}

export function useSelectWithInfiniteScroll<T extends IRecord>(list: ListFuc<T>): Hook<T> {
  const reqId = useRef(0)
  const [state, dispatch] = useReducer(reducer, {
    status: 'loading',
    query: '',
    after: '',
    pageInfo: {
      hasNextPage: false,
      endCursor: '',
    },
    results: [],
    activeItem: null,
  })
  const debouncedList = useCallback(
    debounce((params: { query: string; queryString: string; after: string }, newReqId: number) => {
      list(params)
        .then((resp) => {
          const { pageInfo, edges } = resp || { pageInfo: {}, edges: [] }
          if (reqId.current === newReqId) {
            dispatch({
              type: 'load',
              pageInfo,
              results: edges.map(({ node }) => node),
            })
          }
        })
        .catch((error) => Toaster.fail({ error }))
    }, 300),
    [list]
  )

  useEffect(() => {
    const newReqId = Date.now()
    reqId.current = newReqId
    debouncedList({ query: state.query, queryString: state.query, after: state.after }, newReqId)
    return () => debouncedList.cancel()
  }, [state.query, state.after, debouncedList])

  /* eslint-disable @typescript-eslint/ban-ts-comment */
  // @ts-ignore
  return {
    ...state,
    run(query: string) {
      dispatch({ type: 'search', query })
    },
    next() {
      if (state.pageInfo.hasNextPage && state.status === 'idle') {
        dispatch({ type: 'page' })
      }
    },
    setActiveItem(activeItem: T | null) {
      dispatch({ type: 'activeItemUpdate', activeItem })
    },
    reset() {
      dispatch({
        type: 'reset',
        pageInfo: {
          hasNextPage: false,
          endCursor: '',
        },
        results: [],
        after: '',
        query: '',
        activeItem: null,
      })
    },
  }
}

function reducer<T>(state: State<T>, action: Action<T>) {
  switch (action.type) {
    case 'search':
      return { ...state, query: action.query, after: '', status: 'loading' }

    case 'load':
      return {
        ...state,
        status: 'idle',
        pageInfo: action.pageInfo,
        results: state.after ? [...state.results, ...action.results] : action.results,
      }

    case 'page':
      return {
        ...state,
        status: 'paging',
        after: state.pageInfo.endCursor,
      }

    case 'activeItemUpdate':
      return {
        ...state,
        activeItem: action.activeItem,
      }

    case 'reset':
      return {
        ...state,
        status: 'idle',
        query: '',
        pageInfo: action.pageInfo,
        results: [],
      }

    default:
      return state
  }
}
